import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface HelpCategory {
  id: string;
  title: string;
  icon: string;
  topics: HelpTopic[];
}

interface HelpTopic {
  id: string;
  title: string;
  content: string;
}

const HelpCenterPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('getting-started');
  
  const helpCategories: HelpCategory[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: '🚀',
      topics: [
        {
          id: 'create-account',
          title: 'How to create an account',
          content: `
            <h3>Creating Your Quick Flick Account</h3>
            <p>Follow these simple steps to create your Quick Flick account:</p>
            <ol>
              <li>Go to the Quick Flick homepage at <a href="/">quickflick.com</a></li>
              <li>Click on the "Sign In" button in the top right corner</li>
              <li>Click "Sign up now" at the bottom of the sign-in form</li>
              <li>Enter your email address and create a password</li>
              <li>Select your preferred subscription plan</li>
              <li>Enter your payment details</li>
              <li>Start streaming!</li>
            </ol>
            <p>Need more help? <a href="/contact">Contact our support team</a>.</p>
          `
        },
        {
          id: 'subscription-plans',
          title: 'Subscription plans explained',
          content: `
            <h3>Quick Flick Subscription Plans</h3>
            <p>We offer three main subscription plans to meet your needs:</p>
            <h4>Basic Plan - $8.99/month</h4>
            <ul>
              <li>Standard Definition (SD) quality</li>
              <li>Watch on 1 device at a time</li>
              <li>Access to all content (with ads)</li>
            </ul>
            <h4>Standard Plan - $13.99/month</h4>
            <ul>
              <li>High Definition (HD) quality</li>
              <li>Watch on 2 devices at a time</li>
              <li>Ad-free viewing experience</li>
            </ul>
            <h4>Premium Plan - $17.99/month</h4>
            <ul>
              <li>Ultra HD/4K quality (where available)</li>
              <li>Watch on 4 devices at a time</li>
              <li>Ad-free viewing experience</li>
              <li>Offline downloads</li>
            </ul>
            <p>You can change your plan at any time by going to your Account settings.</p>
          `
        }
      ]
    },
    {
      id: 'account-billing',
      title: 'Account & Billing',
      icon: '💳',
      topics: [
        {
          id: 'change-plan',
          title: 'How to change your plan',
          content: `
            <h3>Changing Your Subscription Plan</h3>
            <p>You can upgrade or downgrade your plan at any time:</p>
            <ol>
              <li>Sign in to your Quick Flick account</li>
              <li>Go to your Profile page</li>
              <li>Click on "Subscription" in the sidebar</li>
              <li>Click "Change Plan"</li>
              <li>Select your new plan</li>
              <li>Confirm your selection</li>
            </ol>
            <p>Your plan change will take effect immediately. If you upgrade, you'll be charged the prorated difference for the remainder of your billing cycle. If you downgrade, the new rate will apply on your next billing date.</p>
          `
        },
        {
          id: 'payment-methods',
          title: 'Managing payment methods',
          content: `
            <h3>Managing Your Payment Methods</h3>
            <p>Quick Flick accepts the following payment methods:</p>
            <ul>
              <li>Credit cards (Visa, MasterCard, American Express, Discover)</li>
              <li>Debit cards</li>
              <li>PayPal</li>
              <li>Gift cards</li>
            </ul>
            <p>To add or update your payment method:</p>
            <ol>
              <li>Sign in to your Quick Flick account</li>
              <li>Go to your Profile page</li>
              <li>Click on "Subscription" in the sidebar</li>
              <li>Select "Payment Methods"</li>
              <li>Click "Add Payment Method" or edit an existing one</li>
            </ol>
          `
        }
      ]
    },
    {
      id: 'watching',
      title: 'Watching Quick Flick',
      icon: '📺',
      topics: [
        {
          id: 'video-quality',
          title: 'Adjusting video quality',
          content: `
            <h3>Adjusting Video Quality</h3>
            <p>You can adjust the video quality while watching content:</p>
            <ol>
              <li>While a video is playing, click the settings icon (gear)</li>
              <li>Select "Quality" from the menu</li>
              <li>Choose your preferred quality setting</li>
            </ol>
            <p>Note: The maximum video quality depends on your subscription plan, device capabilities, and internet connection speed.</p>
            <p>To set your default video quality:</p>
            <ol>
              <li>Go to your Account settings</li>
              <li>Select "Playback Settings"</li>
              <li>Choose your preferred quality</li>
              <li>Save your changes</li>
            </ol>
          `
        },
        {
          id: 'subtitles',
          title: 'Using subtitles and closed captions',
          content: `
            <h3>Using Subtitles and Closed Captions</h3>
            <p>To turn on subtitles or closed captions:</p>
            <ol>
              <li>While a video is playing, click the subtitle icon (speech bubble)</li>
              <li>Select your preferred language from the list</li>
              <li>To turn off subtitles, select "Off"</li>
            </ol>
            <p>You can customize the appearance of subtitles in your account settings:</p>
            <ol>
              <li>Go to your Account settings</li>
              <li>Select "Subtitle Appearance"</li>
              <li>Adjust font, size, color, and background</li>
              <li>Save your changes</li>
            </ol>
          `
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: '🔧',
      topics: [
        {
          id: 'streaming-issues',
          title: 'Fixing streaming issues',
          content: `
            <h3>Fixing Streaming Issues</h3>
            <p>If you're experiencing buffering or poor video quality:</p>
            <h4>1. Check your internet connection</h4>
            <p>Make sure you have a stable internet connection. We recommend:</p>
            <ul>
              <li>3 Mbps for SD quality</li>
              <li>5 Mbps for HD quality</li>
              <li>25 Mbps for 4K/Ultra HD quality</li>
            </ul>
            <p>You can test your connection speed at <a href="https://www.speedtest.net" target="_blank" rel="noopener noreferrer">speedtest.net</a>.</p>
            
            <h4>2. Restart your device and router</h4>
            <p>Turn off your streaming device and router for 30 seconds, then turn them back on.</p>
            
            <h4>3. Close other applications and devices</h4>
            <p>Other apps and devices using your network can impact streaming performance.</p>
            
            <h4>4. Try a different device</h4>
            <p>If possible, try streaming on a different device to see if the issue persists.</p>
            
            <h4>5. Clear cache and cookies</h4>
            <p>If you're watching on a web browser, clearing your cache and cookies might help.</p>
            
            <p>Still having issues? <a href="/contact">Contact our support team</a>.</p>
          `
        },
        {
          id: 'login-issues',
          title: 'Resolving login problems',
          content: `
            <h3>Resolving Login Problems</h3>
            <p>If you're having trouble logging in to your Quick Flick account:</p>
            
            <h4>Forgotten Password</h4>
            <ol>
              <li>Go to the login page</li>
              <li>Click "Forgot Password"</li>
              <li>Enter the email address associated with your account</li>
              <li>Follow the instructions sent to your email to reset your password</li>
            </ol>
            
            <h4>Wrong Email or Password</h4>
            <p>Make sure you're using the correct email address and password. Passwords are case-sensitive.</p>
            
            <h4>Account Locked</h4>
            <p>Your account might be temporarily locked after too many failed login attempts. Wait 30 minutes and try again, or reset your password.</p>
            
            <h4>Payment Issues</h4>
            <p>If your payment method has expired or been declined, your account might be on hold. Check your payment information in your account settings.</p>
            
            <h4>Still Can't Log In?</h4>
            <p>Contact our support team with the following information:</p>
            <ul>
              <li>The email address associated with your account</li>
              <li>The device you're trying to log in from</li>
              <li>Any error messages you're seeing</li>
            </ul>
          `
        }
      ]
    }
  ];
  
  const selectedCategoryData = helpCategories.find(category => category.id === selectedCategory) || helpCategories[0];
  const [selectedTopic, setSelectedTopic] = useState<string>(selectedCategoryData.topics[0].id);
  
  const selectedTopicData = selectedCategoryData.topics.find(topic => topic.id === selectedTopic) || selectedCategoryData.topics[0];
  
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    const category = helpCategories.find(cat => cat.id === categoryId) || helpCategories[0];
    setSelectedTopic(category.topics[0].id);
  };
  
  return (
    <main>
      <div className="container">
        <div style={{ marginTop: '120px', marginBottom: '50px' }}>
          <h1 className="section-title">Help Center</h1>
          
          <div style={{ marginTop: '30px' }}>
            <div style={{ 
              marginBottom: '30px',
              background: 'var(--secondary-color)',
              borderRadius: '8px',
              padding: '25px',
              textAlign: 'center'
            }}>
              <h2 style={{ marginBottom: '15px', fontSize: '1.3rem' }}>How can we help you?</h2>
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <input
                  type="text"
                  placeholder="Search for help..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    width: '100%',
                    padding: '12px 15px',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    color: 'var(--text-color)',
                    marginBottom: '15px',
                    fontSize: '1.1rem'
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                  <Link to="/contact" className="btn btn-primary">Contact Support</Link>
                  <Link to="/faq" className="btn btn-secondary">View FAQs</Link>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '25px' }}>
              <div style={{ 
                flex: '0 0 250px',
                background: 'var(--secondary-color)',
                borderRadius: '8px',
                padding: '20px'
              }}>
                <h2 style={{ marginBottom: '20px', fontSize: '1.2rem' }}>Help Topics</h2>
                <ul style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {helpCategories.map(category => (
                    <li 
                      key={category.id}
                      style={{ 
                        cursor: 'pointer',
                        padding: '10px',
                        borderRadius: '4px',
                        backgroundColor: selectedCategory === category.id ? 'var(--primary-color)' : 'transparent'
                      }}
                      onClick={() => handleCategoryChange(category.id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>{category.icon}</span>
                        <span>{category.title}</span>
                      </div>
                    </li>
                  ))}
                </ul>
                
                <div style={{ 
                  marginTop: '30px',
                  padding: '15px',
                  borderTop: '1px solid var(--gray-color)',
                  textAlign: 'center'
                }}>
                  <h3 style={{ marginBottom: '10px', fontSize: '1rem' }}>Need More Help?</h3>
                  <Link 
                    to="/contact" 
                    className="btn btn-primary"
                    style={{ width: '100%', fontSize: '0.9rem' }}
                  >
                    Contact Support
                  </Link>
                </div>
              </div>
              
              <div style={{ flex: '1 1 300px' }}>
                <div style={{ 
                  background: 'var(--secondary-color)',
                  borderRadius: '8px',
                  padding: '25px',
                  marginBottom: '20px'
                }}>
                  <h2 style={{ marginBottom: '15px', fontSize: '1.3rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span>{selectedCategoryData.icon}</span>
                    <span>{selectedCategoryData.title}</span>
                  </h2>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                    {selectedCategoryData.topics.map(topic => (
                      <button
                        key={topic.id}
                        className={`category-item ${selectedTopic === topic.id ? 'active' : ''}`}
                        style={{ 
                          backgroundColor: selectedTopic === topic.id ? 'var(--primary-color)' : '',
                          cursor: 'pointer'
                        }}
                        onClick={() => setSelectedTopic(topic.id)}
                      >
                        {topic.title}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div style={{ 
                  background: 'var(--secondary-color)',
                  borderRadius: '8px',
                  padding: '25px'
                }}>
                  <h3 style={{ marginBottom: '20px', fontSize: '1.2rem' }}>{selectedTopicData.title}</h3>
                  
                  <div 
                    style={{ lineHeight: '1.6' }}
                    dangerouslySetInnerHTML={{ __html: selectedTopicData.content }} 
                  />
                  
                  <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <p style={{ marginBottom: '15px', color: 'var(--light-gray)' }}>Was this article helpful?</p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                      <button className="btn btn-secondary" style={{ width: '80px' }}>Yes</button>
                      <button className="btn btn-secondary" style={{ width: '80px' }}>No</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default HelpCenterPage; 